/**
 * Storage Service
 * Manages user data with hybrid local/cloud storage
 * Supports both authenticated (cloud) and guest (local) modes
 */

import { API_URL } from '../config';

const STORAGE_KEYS = {
  CARDS: 'card_benefits_cards',
  USER_PREFS: 'card_benefits_prefs',
  SEARCH_HISTORY: 'card_benefits_history'
};

// ==================== Local Storage Operations ====================

const localStorageOps = {
  getCards() {
    try {
      const cards = localStorage.getItem(STORAGE_KEYS.CARDS);
      return cards ? JSON.parse(cards) : [];
    } catch (error) {
      console.error('Error reading cards from local storage:', error);
      return [];
    }
  },

  saveCards(cards) {
    try {
      localStorage.setItem(STORAGE_KEYS.CARDS, JSON.stringify(cards));
      return true;
    } catch (error) {
      console.error('Error saving cards to local storage:', error);
      return false;
    }
  },

  addCard(card) {
    const cards = this.getCards();
    cards.push(card);
    return this.saveCards(cards);
  },

  deleteCard(cardId) {
    const cards = this.getCards().filter(c => c.id !== cardId);
    return this.saveCards(cards);
  },

  getPreferences() {
    try {
      const prefs = localStorage.getItem(STORAGE_KEYS.USER_PREFS);
      return prefs ? JSON.parse(prefs) : {};
    } catch (error) {
      console.error('Error reading preferences:', error);
      return {};
    }
  },

  savePreferences(prefs) {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_PREFS, JSON.stringify(prefs));
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      return false;
    }
  },

  clearAll() {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('Error clearing local storage:', error);
      return false;
    }
  }
};

// ==================== Cloud Storage Operations ====================

const cloudStorageOps = {
  async getCards(authToken) {
    const response = await fetch(`${API_URL}/api/user/cards`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cards from cloud');
    }

    const data = await response.json();
    return data.cards || [];
  },

  async saveCard(authToken, card) {
    const response = await fetch(`${API_URL}/api/user/cards`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(card)
    });

    if (!response.ok) {
      throw new Error('Failed to save card to cloud');
    }

    return response.json();
  },

  async deleteCard(authToken, cardId) {
    const response = await fetch(`${API_URL}/api/user/cards/${cardId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to delete card from cloud');
    }

    return response.json();
  },

  async syncCards(authToken, cards) {
    const response = await fetch(`${API_URL}/api/user/cards/sync`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ cards })
    });

    if (!response.ok) {
      throw new Error('Failed to sync cards to cloud');
    }

    return response.json();
  },

  async getSearchHistory(authToken, limit = 20) {
    const response = await fetch(`${API_URL}/api/user/history?limit=${limit}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch search history');
    }

    const data = await response.json();
    return data.history || [];
  },

  async clearSearchHistory(authToken) {
    const response = await fetch(`${API_URL}/api/user/history`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to clear search history');
    }

    return response.json();
  }
};

// ==================== Unified Storage Service ====================

export const storageService = {
  /**
   * Get cards - from cloud if authenticated, otherwise local
   * @param {string|null} authToken - JWT token if authenticated
   * @returns {Promise<Array>|Array} Cards array
   */
  async getCards(authToken = null) {
    if (authToken) {
      try {
        return await cloudStorageOps.getCards(authToken);
      } catch (error) {
        console.error('Cloud fetch failed, falling back to local:', error);
        return localStorageOps.getCards();
      }
    }
    return localStorageOps.getCards();
  },

  /**
   * Get local cards only (for migration check)
   * @returns {Array} Local cards
   */
  getLocalCards() {
    return localStorageOps.getCards();
  },

  /**
   * Save all cards - to cloud if authenticated, always to local as backup
   * @param {Array} cards - Cards to save
   * @param {string|null} authToken - JWT token if authenticated
   */
  async saveCards(cards, authToken = null) {
    // Always save locally as backup
    localStorageOps.saveCards(cards);

    // If authenticated, sync to cloud
    if (authToken) {
      try {
        await cloudStorageOps.syncCards(authToken, cards);
      } catch (error) {
        console.error('Cloud sync failed:', error);
      }
    }

    return true;
  },

  /**
   * Add a single card
   * @param {Object} card - Card to add
   * @param {string|null} authToken - JWT token if authenticated
   */
  async addCard(card, authToken = null) {
    // Always save locally
    localStorageOps.addCard(card);

    // If authenticated, save to cloud
    if (authToken) {
      try {
        await cloudStorageOps.saveCard(authToken, card);
      } catch (error) {
        console.error('Cloud save failed:', error);
      }
    }

    return { success: true, card_id: card.id };
  },

  /**
   * Delete a card
   * @param {string} cardId - Card ID to delete
   * @param {string|null} authToken - JWT token if authenticated
   */
  async deleteCard(cardId, authToken = null) {
    // Always delete locally
    localStorageOps.deleteCard(cardId);

    // If authenticated, delete from cloud
    if (authToken) {
      try {
        await cloudStorageOps.deleteCard(authToken, cardId);
      } catch (error) {
        console.error('Cloud delete failed:', error);
      }
    }

    return { success: true };
  },

  /**
   * Migrate local cards to cloud
   * @param {string} authToken - JWT token (required)
   */
  async migrateToCloud(authToken) {
    if (!authToken) {
      throw new Error('Authentication required for migration');
    }

    const localCards = localStorageOps.getCards();
    if (localCards.length === 0) {
      return { migrated: 0 };
    }

    try {
      await cloudStorageOps.syncCards(authToken, localCards);
      return { migrated: localCards.length };
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    }
  },

  /**
   * Get search history (cloud only)
   * @param {string} authToken - JWT token
   * @param {number} limit - Max results
   */
  async getSearchHistory(authToken, limit = 20) {
    if (!authToken) {
      return [];
    }

    try {
      return await cloudStorageOps.getSearchHistory(authToken, limit);
    } catch (error) {
      console.error('Failed to get search history:', error);
      return [];
    }
  },

  /**
   * Clear search history (cloud only)
   * @param {string} authToken - JWT token
   */
  async clearSearchHistory(authToken) {
    if (!authToken) {
      return { success: false };
    }

    try {
      return await cloudStorageOps.clearSearchHistory(authToken);
    } catch (error) {
      console.error('Failed to clear search history:', error);
      return { success: false };
    }
  },

  // Preferences (local only for now)
  getPreferences: localStorageOps.getPreferences,
  savePreferences: localStorageOps.savePreferences,
  clearAll: localStorageOps.clearAll
};

// ==================== API Service ====================

export const apiService = {
  async getRecommendations(merchant, cards, authToken = null) {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_URL}/api/recommend`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ merchant, cards })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch recommendations');
    }

    return response.json();
  },

  async getCardBenefits(cardNetwork, bank) {
    const response = await fetch(`${API_URL}/api/benefits?network=${cardNetwork}&bank=${bank}`);

    if (!response.ok) {
      throw new Error('Failed to fetch card benefits');
    }

    return response.json();
  }
};

// Export individual operations for advanced use
export { localStorageOps, cloudStorageOps };

// ==================== Benefits API ====================

export const benefitsService = {
  /**
   * Fetch enriched benefits for all of the user's cards.
   * Returns { benefits, alerts, count, timestamp }
   */
  async getCardBenefits(authToken, forceRefresh = false) {
    const url = `${API_URL}/api/user/benefits${forceRefresh ? '?force_refresh=true' : ''}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const err = new Error('Failed to fetch card benefits');
      err.status = response.status;
      throw err;
    }
    return response.json();
  },

  /**
   * Ask the backend to pre-seed benefits for all of the user's cards (fire-and-forget).
   */
  async prefetchBenefits(authToken) {
    try {
      await fetch(`${API_URL}/api/user/benefits/prefetch`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } catch (_) {
      // best-effort — silently swallow
    }
  },
};
